import config from "console_config";
import client from "Libs/platform";

import type {
  ProjectConfiguration,
  Topology
} from "Reducers/project/configurationWizard/types";

const AI_CONFIGURE_ENDPOINT = `${config.URL_API_PROXY}/ai/configure-project`;

type StreamMessage = {
  type: "log" | "data" | "error" | "done" | "keep_alive" | "output_chunk";
  message?: string;
  level?: string;
  tags?: string[];
  key?: string;
  code?: string;
  data?: Record<string, unknown>;
};

const fetchStream = async (
  projectId: string,
  abortSignal?: AbortSignal
): Promise<Response> => {
  const accessToken = await client.getAccessToken();

  const response = await fetch(AI_CONFIGURE_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken.access_token}`
    },
    body: JSON.stringify({ project_id: projectId }),
    signal: abortSignal
  });

  return response;
};

const parseStream = async (
  response: Response,
  onProgress?: (message: string) => void
): Promise<ProjectConfiguration> => {
  if (!response.body) {
    throw new Error("Response body is empty");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let buffer = "";
  let streamComplete = false;
  const result: ProjectConfiguration = {
    configYaml: "",
    valid: true,
    topology: { webapps: {}, workers: {}, services: {} }
  };

  const handleLine = (line: string) => {
    if (!line.trim()) return;

    let msg: StreamMessage;
    try {
      msg = JSON.parse(line);
    } catch {
      return;
    }

    switch (msg.type) {
      case "data":
        if (msg.key === "output" && msg.data) {
          if (typeof msg.data.config_yaml === "string") {
            result.configYaml = msg.data.config_yaml;
          }
          if (typeof msg.data.valid === "boolean") {
            result.valid = msg.data.valid;
          }
        } else if (msg.key === "topology" && msg.data) {
          const t = msg.data;
          if (
            t.webapps &&
            typeof t.webapps === "object" &&
            t.workers &&
            typeof t.workers === "object" &&
            t.services &&
            typeof t.services === "object"
          ) {
            result.topology = t as unknown as Topology;
          }
        }
        break;

      case "error":
        throw new Error(msg.message || "Configuration generation failed");

      case "log":
        if (msg.level === "error") {
          throw new Error(msg.message || "Configuration generation failed");
        }
        if (msg.tags?.includes("spin") && msg.message) {
          onProgress?.(msg.message);
        }
        break;

      case "done":
        streamComplete = true;
        break;

      case "keep_alive":
      case "output_chunk":
        break;
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        handleLine(line);
      }
    }

    if (buffer.trim()) {
      handleLine(buffer);
    }
  } finally {
    await reader.cancel();
    reader.releaseLock();
  }

  if (!streamComplete || !result.configYaml) {
    throw new Error("Configuration generation was interrupted");
  }

  return result;
};

export const getProjectConfiguration = async (
  projectId: string,
  abortSignal?: AbortSignal,
  onProgress?: (message: string) => void
): Promise<ProjectConfiguration> => {
  let response = await fetchStream(projectId, abortSignal);

  // Retry once on 401 — force re-authentication and try again.
  // Matches the retry behavior in authenticatedRequest from @packages/client.
  if (response.status === 401) {
    await client.reAuthenticate();
    response = await fetchStream(projectId, abortSignal);
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // Use default error message.
    }
    throw new Error(errorMessage);
  }

  return parseStream(response, onProgress);
};
